import { useState, useEffect, useCallback } from 'react';
import { AssetRetrievalService, GeneratedAsset, SessionWithAssets } from '../services/assetRetrievalService';
import { useAuthStore } from '../stores/AuthStore';

export interface UseAssetRetrievalOptions {
  agentType: string;
  sessionBased: boolean;
  autoFetch?: boolean;
  limit?: number;
}

export interface UseAssetRetrievalResult {
  assets: GeneratedAsset[];
  sessions: SessionWithAssets[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  fetchMore: () => Promise<void>;
  hasMore: boolean;
}

/**
 * Custom hook for retrieving persisted assets and sessions
 * Handles both session-based and asset-based agents
 */
export function useAssetRetrieval(options: UseAssetRetrievalOptions): UseAssetRetrievalResult {
  const { agentType, sessionBased, autoFetch = true, limit = 100 } = options;
  const { user } = useAuthStore();

  const [assets, setAssets] = useState<GeneratedAsset[]>([]);
  const [sessions, setSessions] = useState<SessionWithAssets[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const fetchData = useCallback(async (isLoadMore: boolean = false) => {
    if (!user?.id) {
      setError('User not authenticated');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (sessionBased) {
        // Session-based agent: fetch sessions with assets
        const sessionData = await AssetRetrievalService.getUserSessionsByAgent(
          user.id,
          agentType,
          { limit: limit }
        );

        if (isLoadMore) {
          setSessions(prev => [...prev, ...sessionData]);
        } else {
          setSessions(sessionData);
        }

        // Extract all assets from sessions
        const allAssets = sessionData.flatMap(s => s.assets);
        if (isLoadMore) {
          setAssets(prev => [...prev, ...allAssets]);
        } else {
          setAssets(allAssets);
        }

        setHasMore(sessionData.length === limit);
      } else {
        // Asset-based agent: fetch assets directly with server-side filtering
        const assetData = await AssetRetrievalService.getUserAssets(
          user.id,
          {
            agentType: agentType,  // Pass agentType to backend for server-side filtering
            limit: limit
          }
        );

        // No need for client-side filtering - backend already filtered by agentType
        if (isLoadMore) {
          setAssets(prev => [...prev, ...assetData]);
        } else {
          setAssets(assetData);
        }

        setHasMore(assetData.length === limit);
      }
    } catch (err: any) {
      console.error('Error fetching assets:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [user?.id, agentType, sessionBased, limit]);

  const refetch = useCallback(async () => {
    setOffset(0);
    await fetchData(false);
  }, [fetchData]);

  const fetchMore = useCallback(async () => {
    if (!hasMore || loading) return;
    setOffset(prev => prev + limit);
    await fetchData(true);
  }, [hasMore, loading, limit, fetchData]);

  useEffect(() => {
    if (autoFetch && user?.id) {
      fetchData(false);
    }
  }, [autoFetch, user?.id, agentType, sessionBased]);

  return {
    assets,
    sessions,
    loading,
    error,
    refetch,
    fetchMore,
    hasMore,
  };
}

/**
 * Hook for fetching a specific session with assets
 */
export function useSession(sessionId: string | null) {
  const [session, setSession] = useState<SessionWithAssets | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSession = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setError(null);

    try {
      const sessionData = await AssetRetrievalService.getSession(sessionId);
      setSession(sessionData);
    } catch (err: any) {
      console.error('Error fetching session:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch session');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchSession();
    }
  }, [sessionId, fetchSession]);

  return {
    session,
    loading,
    error,
    refetch: fetchSession,
  };
}

/**
 * Hook for fetching user statistics
 */
export function useUserStats() {
  const { user } = useAuthStore();
  const [assetStats, setAssetStats] = useState<any>(null);
  const [sessionStats, setSessionStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [assets, sessions] = await Promise.all([
        AssetRetrievalService.getUserAssetStats(user.id),
        AssetRetrievalService.getUserSessionStats(user.id),
      ]);

      setAssetStats(assets);
      setSessionStats(sessions);
    } catch (err: any) {
      console.error('Error fetching stats:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchStats();
    }
  }, [user?.id, fetchStats]);

  return {
    assetStats,
    sessionStats,
    loading,
    error,
    refetch: fetchStats,
  };
}

export default useAssetRetrieval;
